import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import CircularProgress from '@mui/material/CircularProgress'
import SendIcon from '@mui/icons-material/ArrowUpward'
import { askLibrary, searchHighlights, ApiError } from '../lib/api'
import type { AskResponse, SearchResult } from '../types/models'
import { Eyebrow } from '../components/ui'
import { useSubscription } from '../hooks/useSubscription'
import { UpgradeCard } from '../components/Premium'

type Mode = 'ask' | 'search'

export default function Ask() {
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState<Mode>('ask')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answer, setAnswer] = useState<AskResponse | null>(null)
  const [results, setResults] = useState<SearchResult[]>([])
  const { subscription, loading: subLoading } = useSubscription()
  const [gated, setGated] = useState(false)
  // Ask is a Premium feature. Gate up front once we know the plan; a 402 is a fallback.
  const showGate = gated || (!subLoading && subscription?.active !== true)

  async function submit() {
    const q = query.trim()
    if (!q || loading) return
    setLoading(true)
    setError(null)
    setAnswer(null)
    setResults([])
    try {
      if (mode === 'ask') {
        setAnswer(await askLibrary(q))
      } else {
        setResults(await searchHighlights(q))
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'PAYMENT_REQUIRED') {
        setGated(true)
      } else {
        setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Eyebrow moss>Ask</Eyebrow>
      <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 'clamp(26px,3.5vw,36px)', letterSpacing: '-0.6px', mt: 0.5, mb: 0.5 }}>
        Ask <em style={{ color: 'var(--moss)' }}>your library.</em>
      </Typography>
      <Typography sx={{ color: 'var(--ink-2)', mb: 3 }}>
        Answered from your own highlights — always cited. Never the open internet.
      </Typography>

      {showGate ? (
        <UpgradeCard title="Ask is part of Premium" feature="Ask, answered from your own library" />
      ) : (
        <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={mode === 'ask' ? 'Ask a question…' : 'Search your highlights…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit()
            }
          }}
        />
        <IconButton
          onClick={submit}
          disabled={!query.trim() || loading}
          sx={{ mt: '4px', bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      <ToggleButtonGroup
        value={mode}
        exclusive
        size="small"
        onChange={(_, v: Mode | null) => v && setMode(v)}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="ask" sx={{ textTransform: 'none' }}>Ask</ToggleButton>
        <ToggleButton value="search" sx={{ textTransform: 'none' }}>Search</ToggleButton>
      </ToggleButtonGroup>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ ...cardSx, borderColor: 'var(--danger)' }}>
          <Typography sx={{ color: 'var(--danger)' }}>{error}</Typography>
        </Box>
      )}

      {answer && (
        <Box sx={{ ...cardSx, mb: 2 }}>
          <Eyebrow moss>✦ What your highlights suggest</Eyebrow>
          {/* overflowWrap: a long unbroken token (e.g. a URL) would widen this fixed-width card past the viewport. */}
          <Typography sx={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink)', mt: 1.5, overflowWrap: 'anywhere' }}>{answer.answer}</Typography>
          {answer.sources.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Eyebrow>Sources</Eyebrow>
              <Box sx={{ display: 'grid', gap: 1.5, mt: 1.5 }}>
                {answer.sources.map((s) => (
                  <SourceCard key={s.id} result={s} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {results.length > 0 && (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {results.map((s) => (
            <SourceCard key={s.id} result={s} />
          ))}
        </Box>
      )}

      {!loading && !answer && results.length === 0 && !error && (
        <Typography sx={{ color: 'var(--ink-3)', textAlign: 'center', py: 6 }}>
          Ask about anything you've read.
        </Typography>
      )}
        </>
      )}
    </Box>
  )
}

function SourceCard({ result }: { result: SearchResult }) {
  return (
    <Box sx={{ ...cardSx, p: 2, borderLeft: '3px solid var(--moss)' }}>
      {/* Imported highlight text: same overflowWrap guard as the AI-answer text above. */}
      <Typography sx={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 16, color: 'var(--ink)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
        {result.text}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Eyebrow>{result.bookTitle} · {result.chapterTitle}</Eyebrow>
      </Box>
    </Box>
  )
}

const cardSx = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-lg)',
  p: 3,
} as const
